import React from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TabsContent } from "@/components/ui/tabs";
import { formatTime12h, getTodayString } from "../eventTime";

export const AttendeesTab = (props) => {
  const {
    attendanceRequests,
    attendeeSearch,
    setAttendeeSearch,
    attendeeSortField,
    attendeeSortDir,
    setAttendeeSortField,
    setAttendeeSortDir,
  } = props;
  return (
          <TabsContent value="attendees" data-testid="attendees-content">
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
              <div className="p-4 border-b border-zinc-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-crew-yellow">APPROVED ATTENDEES</h2>
                  <p className="text-zinc-500 text-sm">
                    {attendanceRequests.filter(r => r.status === 'approved').length} approved attendees • Click column headers to sort
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    type="text"
                    placeholder="Search by name or event..."
                    value={attendeeSearch}
                    onChange={(e) => setAttendeeSearch(e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-white w-64 h-9"
                    data-testid="attendee-search"
                  />
                </div>
              </div>

              {(() => {
                // Filter to only approved attendance and apply search
                const approvedAttendees = attendanceRequests.filter(r => {
                  if (r.status !== 'approved') return false;
                  if (!attendeeSearch) return true;
                  const search = attendeeSearch.toLowerCase();
                  return (
                    r.participant_name?.toLowerCase().includes(search) ||
                    r.event_name?.toLowerCase().includes(search) ||
                    r.participant_email?.toLowerCase().includes(search)
                  );
                });

                // Default ("upcoming-first"): today & next-upcoming at the top, then
                // future events soonest-first, then past events (most-recent first) below.
                const today = getTodayString();
                const upcomingFirst = (a, b) => {
                  const da = a.event_date || '9999-99-99';
                  const db = b.event_date || '9999-99-99';
                  const aFuture = da >= today;
                  const bFuture = db >= today;
                  if (aFuture !== bFuture) return aFuture ? -1 : 1;
                  if (da !== db) return aFuture ? da.localeCompare(db) : db.localeCompare(da);
                  return (a.participant_name || '').localeCompare(b.participant_name || '');
                };

                const sortedAttendees = [...approvedAttendees].sort((a, b) => {
                  // Column-header sorting: event_date keeps the upcoming-first logic (toggle
                  // flips it); other columns sort alphabetically/numerically.
                  if (!attendeeSortField || attendeeSortField === 'event_date') {
                    const cmp = upcomingFirst(a, b);
                    return attendeeSortDir === 'desc' ? -cmp : cmp;
                  }
                  const av = (a[attendeeSortField] ?? '').toString();
                  const bv = (b[attendeeSortField] ?? '').toString();
                  const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
                  return attendeeSortDir === 'desc' ? -cmp : cmp;
                });

                const handleAttendeeSort = (field) => {
                  if (attendeeSortField === field) {
                    setAttendeeSortDir(attendeeSortDir === 'asc' ? 'desc' : 'asc');
                  } else {
                    setAttendeeSortField(field);
                    setAttendeeSortDir('asc');
                  }
                };

                const AttendeeSortHeader = ({ field, children }) => (
                  <th 
                    className="py-3 px-4 text-left text-xs font-bold uppercase text-zinc-400 cursor-pointer hover:text-crew-yellow select-none"
                    onClick={() => handleAttendeeSort(field)}
                  >
                    <div className="flex items-center gap-1">
                      {children}
                      {attendeeSortField === field && (
                        attendeeSortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                );

                if (sortedAttendees.length === 0) {
                  return (
                    <div className="p-12 text-center text-zinc-500" data-testid="no-attendee-data">
                      {attendeeSearch ? 'No matching attendees found.' : 'No approved attendees yet.'}
                    </div>
                  );
                }

                return (
                  <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-900 sticky top-0 z-10">
                        <tr className="border-b border-zinc-700">
                          <AttendeeSortHeader field="event_date">Event Date</AttendeeSortHeader>
                          <AttendeeSortHeader field="call_time">Call Time</AttendeeSortHeader>
                          <AttendeeSortHeader field="event_name">Event Name</AttendeeSortHeader>
                          <AttendeeSortHeader field="participant_name">Attendee Name</AttendeeSortHeader>
                          <th className="py-3 px-4 text-left text-xs font-bold uppercase text-zinc-400">Email</th>
                          <th className="py-3 px-4 text-left text-xs font-bold uppercase text-zinc-400">Phone</th>
                          <th className="py-3 px-4 text-left text-xs font-bold uppercase text-zinc-400">Reminder</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800">
                        {sortedAttendees.map((attendee, idx) => {
                          const isEventToday = attendee.event_date === today;
                          const isPastEvent = attendee.event_date < today;
                          
                          return (
                            <tr 
                              key={attendee.id || idx} 
                              className={`hover:bg-zinc-900/50 ${isEventToday ? 'bg-crew-yellow/10' : ''} ${isPastEvent ? 'opacity-60' : ''}`}
                              data-testid={`attendee-row-${idx}`}
                            >
                              <td className="py-3 px-4">
                                <div className="flex flex-col">
                                  <span className={`font-mono text-sm ${isEventToday ? 'text-crew-yellow font-bold' : 'text-crew-yellow'}`}>
                                    {attendee.event_date || '—'}
                                  </span>
                                  {isEventToday && <span className="text-xs text-crew-yellow font-bold">TODAY</span>}
                                  {isPastEvent && <span className="text-xs text-zinc-500">PAST</span>}
                                </div>
                              </td>
                              <td className="py-3 px-4 font-mono text-crew-yellow font-bold">
                                {formatTime12h(attendee.call_time)}
                              </td>
                              <td className="py-3 px-4 text-white font-medium">
                                {attendee.event_name}
                              </td>
                              <td className="py-3 px-4 text-white">
                                {attendee.participant_name}
                              </td>
                              <td className="py-3 px-4 text-zinc-400">
                                {attendee.participant_email}
                              </td>
                              <td className="py-3 px-4 text-zinc-400 font-mono">
                                {attendee.participant_phone || '—'}
                              </td>
                              <td className="py-3 px-4">
                                {attendee.reminder_sent ? (
                                  <span className="text-xs text-green-400 flex items-center gap-1">
                                    <Check size={12} /> Sent
                                  </span>
                                ) : (
                                  <span className="text-xs text-zinc-500">Pending</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </TabsContent>
  );
};
